import type { DataPageModel } from "@modelve/api/models/dataPageModel";
import { wrapApiError } from "../apiErrors";
import { useEffect, useState } from "react";
import useSWRInfinite from "swr/infinite";
import { createResourceApiKey } from "../cache/apiKey";
import { registerServerPagedCache } from "../cache/serverPagedCacheRegistry";
import type { ApiKey } from "../cache/types";
import { client } from "../client";
import type { ApiResourceRoute } from "../resources/types";
import { useApiStore } from "../store";
import {
  getRequestConfig,
  getSWRConfig,
  mergeApiQueryConfig,
} from "./queryConfig";
import type {
  ServerPagedApiGetOptions,
  ServerPagedApiGetResponse,
} from "./types";

export function useServerPagedApiGet<TEntity>(
  resourceRoute: ApiResourceRoute<TEntity> | null | undefined,
  options: ServerPagedApiGetOptions = {},
): ServerPagedApiGetResponse<TEntity> {
  const { limit = 10, queryParams = {}, ...config } = options;
  const clientType = resourceRoute?.descriptor.clientType;
  useApiStore();

  const [currentPage, setCurrentPage] = useState(0);
  const { disableCache } = mergeApiQueryConfig(config);

  const baseKey = createResourceApiKey(resourceRoute, "serverPagedGet");
  const stableQueryParams = Object.entries(queryParams)
    .sort(([left], [right]) => left.localeCompare(right))
    .map<[string, string]>(([key, value]) => [key, String(value)]);

  const paginationKey = JSON.stringify([
    clientType,
    baseKey?.requestedRoute,
    limit,
    stableQueryParams,
  ]);

  const [activePaginationKey, setActivePaginationKey] = useState(paginationKey);
  if (activePaginationKey !== paginationKey) {
    setActivePaginationKey(paginationKey);
    setCurrentPage(0);
  }

  const { data, mutate, setSize, ...response } = useSWRInfinite<
    DataPageModel<TEntity>
  >(
    (pageIndex, previousPage: DataPageModel<TEntity> | null) => {
      if (!baseKey || (pageIndex > 0 && !previousPage?.hasNextPage)) {
        return null;
      }

      const keyedRoute = baseKey.route;
      const requestedRoute = baseKey.requestedRoute;
      if (
        typeof keyedRoute !== "string" ||
        typeof requestedRoute !== "string"
      ) {
        return null;
      }

      const params = new URLSearchParams(stableQueryParams);
      params.set("limit", String(limit));
      params.set("nextPageToken", previousPage?.paginationToken ?? "");
      const separator = keyedRoute.includes("?") ? "&" : "?";
      const query = `${separator}${params.toString()}`;
      const pageKey: ApiKey = {
        namespace: "serverPagedGet",
        clientType: baseKey.clientType,
        route: `${keyedRoute}${query}`,
        requestedRoute: `${requestedRoute}${query}`,
      };
      if (!baseKey.cacheResource) return pageKey;

      return {
        ...pageKey,
        cacheResource: baseKey.cacheResource,
      };
    },
    async ({ requestedRoute }: ApiKey) => {
      try {
        const result = await client().get<DataPageModel<TEntity>>(
          requestedRoute as string,
          getRequestConfig(disableCache),
        );
        return result.data;
      } catch (error) {
        throw wrapApiError(error);
      }
    },
    {
      ...getSWRConfig(config),
      revalidateFirstPage: false,
      // Active queries are refreshed through their registered bound mutator.
      // An unmounted query has no bound mutator, so refresh it when it returns.
      revalidateOnMount: true,
    },
  );

  const cacheTag = baseKey?.cacheResource?.tag;
  const registeredRoute = baseKey?.route;
  const registeredCollectionRoute = baseKey?.cacheResource?.collectionRoute;

  useEffect(() => {
    if (typeof registeredRoute !== "string") return;
    if (cacheTag == null || clientType == null) return;

    return registerServerPagedCache(
      paginationKey,
      {
        cacheTag,
        clientType,
        route: registeredRoute,
        collectionRoute: registeredCollectionRoute,
      },
      mutate,
    );
  }, [
    cacheTag,
    clientType,
    paginationKey,
    registeredCollectionRoute,
    registeredRoute,
    mutate,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil((data?.[0]?.totalItemsCount ?? 0) / limit),
  );
  // Revalidation can remove the selected page. Adjust state before committing
  // a render, but preserve the selection while page-count data is unavailable.
  if (data?.[0] != null && currentPage >= totalPages) {
    setCurrentPage(totalPages - 1);
  }
  return {
    ...response,
    data: data?.[currentPage]?.items,
    mutate,
    currentPage: currentPage + 1,
    totalPages,
    canReset: !response.isLoading && !response.isValidating && currentPage > 0,
    canNext:
      !response.isLoading &&
      !response.isValidating &&
      currentPage < totalPages - 1,
    canPrev: !response.isLoading && !response.isValidating && currentPage > 0,
    prevPage: () => {
      if (!response.isLoading && !response.isValidating && currentPage > 0) {
        setCurrentPage((state) => state - 1);
      }
    },
    nextPage: () => {
      if (
        !response.isLoading &&
        !response.isValidating &&
        currentPage < totalPages - 1
      ) {
        const nextPage = currentPage + 1;
        void setSize(nextPage + 1).then(() => setCurrentPage(nextPage));
      }
    },
    reset: () => {
      if (!response.isLoading && !response.isValidating && currentPage !== 0) {
        setCurrentPage(0);
      }
    },
  };
}
